import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Table,
  Button,
  Row,
  Col,
  Space,
  Popconfirm,
  Switch,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import {
  getPuestos,
  createPuesto,
  updatePuesto,
} from "../../actions/evaluacionDesempeño";

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "switch" ? <Switch /> : <Input />;
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          valuePropName={inputType === "switch" ? "checked" : "value"}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const ModalPuestos = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [showForm, setShowForm] = useState(false); // Nuevo estado para mostrar/ocultar el formulario
  const [editingKey, setEditingKey] = useState("");

  // Estado global
  const { puestos, loading, error } = useSelector(
    (state) => state.evaluacionDesempeño
  );

  // Cargar puestos si no existen al abrir el modal
  useEffect(() => {
    if (visible && !puestos.length) {
      dispatch(getPuestos());
    }
  }, [visible, dispatch, puestos]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0] || ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Resetear
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filtrar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Cerrar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const isEditing = (record) => record.key === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      nombre: record.nombre,
      activo: record.activo,
    });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = [...puestos];
      const index = newData.findIndex((item) => key === item.key);
      if (index > -1) {
        const item = newData[index];
        const updatedItem = {
          id: item.id, // Asegurarse de incluir el id del registro en el objeto actualizado
          nombre: row.nombre,
          activo: row.activo ? 1 : 0,
        };
        newData.splice(index, 1, updatedItem);
        dispatch(updatePuesto(item.id, updatedItem)).then(() => {
          dispatch(getPuestos()); // Recargar los puestos después de actualizar
        });
        setEditingKey("");
      } else {
        const newItem = {
          id: key, // Asegurarse de incluir el id del registro en el nuevo objeto
          nombre: row.nombre,
          activo: row.activo ? 1 : 0,
        };
        newData.push(newItem);
        dispatch(updatePuesto(newItem.id, newItem)).then(() => {
          dispatch(getPuestos()); // Recargar los puestos después de actualizar
        });
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      ...getColumnSearchProps("nombre"),
      editable: true,
    },
    {
      title: "Activo",
      dataIndex: "activo",
      key: "activo",
      render: (text, record) => (
        <Switch checked={record.activo} disabled />
      ),
      editable: true,
    },
    {
      title: "operation",
      dataIndex: "operation",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Button
              onClick={() => save(record.key)}
              type="primary"
              style={{
                marginRight: 8,
              }}
            >
              Guardar
            </Button>
            <Popconfirm
              title="¿Seguro que quieres cancelar?"
              onConfirm={cancel}
              okText="Sí, cancelar"
              cancelText="No ahora"
            >
              <Button>Cancelar</Button>
            </Popconfirm>
          </span>
        ) : (
          <Button
            type="link"
            disabled={editingKey !== ""}
            onClick={() => edit(record)}
          >
            Editar
          </Button>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === "activo" ? "switch" : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  // Mapeo de puestos para la tabla
  const data = puestos.map((puesto) => ({
    key: puesto.id,
    id: puesto.id, // Asegurarse de incluir el id en los datos de la tabla
    nombre: puesto.nombre,
    activo: puesto.activo,
  }));

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const data = {
          nombre: values.nombre,
          activo: 1, // Establecer el campo activo en 1 al crear un nuevo puesto
        };

        dispatch(createPuesto(data)).then(() => {
          dispatch(getPuestos()); // Recargar los puestos después de crear
        });
        onClose();
      })
      .catch((info) => console.log("Validación fallida:", info));
  };

  const handleNew = () => {
    form.resetFields();
    setShowForm(true);
  };

  if (loading) return <p>Cargando...</p>;
  if (error) return <p>Error al cargar los datos: {error.message}</p>;

  return (
    <Modal
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Administrar Puestos</span>
          {!showForm && (
            <Button type="primary" onClick={handleNew} style={{ marginRight: 32 }}>
              Nuevo
            </Button>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800} // Ajusta el ancho del modal aquí
      footer={null}
    >
      {!showForm ? (
        <>
          <Form form={form} component={false}>
            <Table
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              bordered
              dataSource={data}
              columns={mergedColumns}
              rowClassName="editable-row"
              pagination={{
                onChange: cancel,
                pageSize: 5,
              }}
              style={{ marginTop: 24 }}
            />
          </Form>
        </>
      ) : (
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Nombre" name="nombre">
                <Input placeholder="Ingrese el nombre del puesto" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24} style={{ textAlign: "right" }}>
              <Button
                onClick={() => setShowForm(false)}
                style={{ marginRight: 8 }}
              >
                Cancelar
              </Button>
              <Button type="primary" onClick={handleOk}>
                Guardar
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default ModalPuestos;